<script lang="ts" setup>
	export type PhotoContext = {
		src?: string | ArrayBuffer | null
		file?: string | File
		name?: string
	}

	defineProps<{
		value?: string
		photoSrc?: PhotoContext | null
		format?: string
		id: string
		label: string
		required?: boolean
	}>()
	const loading = shallowRef(false)
	const input = ref<HTMLInputElement | null>(null)
	const emit = defineEmits<{
		(param: 'update:photoSrc', arg: PhotoContext): void
		(param: 'update:value', arg: string): void
		(param: 'proceed', arg?: MouseEvent | PhotoContext): void
		(param: 'cancel', arg: Function): void
	}>()

	const { openFile, uploadImage } = useFileExplorer()
	const fileName = ref('img')
	const removeFile = () => {
		if (input.value) {
			input.value.value = ''
			emit('update:photoSrc', {
				file: '',
				src: '',
				name: ''
			})
			emit('update:value', '')
		}
	}
	const processFile = async (e: Event) => {
		loading.value = true
		try {
			const res = await uploadImage(e, true)
			if (res?.value) {
				emit('update:value', res.value.src)
				emit('update:photoSrc', {
					file: (res.value.blob as File) || '',
					name: (res.value.blob?.name as string) || 'N/A',
					src: res.value.src
				})
				fileName.value = res.value.blob?.name as string
			}
		} finally {
			loading.value = false
		}
	}
</script>

<template>
	<a-form-item :label="label" :name="id" :required="required">
		<div class="relative rounded-lg bg-gray-50 p-3 md:block">
			<div class="relative flex items-center justify-center gap-2.5 rounded-lg bg-gray-100 px-4 py-3">
				<div v-show="loading" class="absolute left-5 top-3">
					<a-spin size="small" spinning />
				</div>
				<div class="w-full max-w-[375px] cursor-pointer" @click="openFile(input)">
					<div class="relative flex flex-col items-center justify-center overflow-hidden">
						<lazy-tools-icon dir="documents" :icon="!photoSrc?.file && !value ? `clipboard` : 'file-img'" />
					</div>

					<div class="flex flex-col items-center justify-center gap-[7px]">
						<div class="flex items-center gap-x-2 pt-1 font-medium">
							<span :class="{ 'text-primary-2': !photoSrc?.file }">
								{{ !photoSrc?.file && !value ? 'Tap to upload' : photoSrc?.name || fileName }}
							</span>

							<button
								v-if="photoSrc?.file || value"
								type="button"
								aria-label="remove-file"
								@click.stop="removeFile"
							>
								<lazy-tools-icon dir="other" icon="trash" path-class="stroke-error-500" />
							</button>
						</div>

						<div
							v-if="!photoSrc?.file && !value"
							class="text-center text-sm font-normal leading-snug text-gray-500"
						>
							File format: jpeg, png, webp
						</div>
					</div>
				</div>
				<input
					:id="id"
					ref="input"
					type="file"
					:accept="format || '.jpg,.png,.jpeg'"
					class="!hidden"
					@change="processFile"
				/>
			</div>
		</div>
	</a-form-item>
</template>
